/**
 * StageManager for Poker Reborn Effects Engine
 *
 * Central registry to find any component by ID or type.
 * Effects use this to resolve targets instead of direct references.
 *
 * @see tasks/active/T35_SIMPLIFIED_PHASE5_PLAN.md
 */

import type { AnimatableTarget } from '@/effects/core/AnimatableTarget';
import { log } from '@/utils/log';

/**
 * Well-known target IDs used throughout the application.
 * Components should use these constants when registering.
 */
export const TargetIds = {
  /** Hero's hole cards */
  HERO_CARDS: 'hero-cards',

  /** Community cards (flop, turn, river) */
  COMMUNITY_CARDS: 'community-cards',

  /** Main pot display */
  POT: 'pot',

  /** Dealer button */
  DEALER_BUTTON: 'dealer-button',

  /** Table view (for ambient effects) */
  TABLE: 'table',

  /**
   * Get seat target ID for a given seat index
   * @param seatIndex - 0-based seat index
   */
  seat: (seatIndex: number) => `seat-${seatIndex}`,

  /**
   * Get player pot target ID for a given seat index
   * @param seatIndex - 0-based seat index
   */
  playerPot: (seatIndex: number) => `player-pot-${seatIndex}`,
} as const;

/**
 * StageManager - Central registry for effect targets
 *
 * Provides a single place to find any component that can be animated.
 * Components register themselves on creation and unregister on destroy.
 *
 * @example
 * ```typescript
 * // In component
 * stageManager.register(makeAnimatableTarget(this, TargetIds.seat(0)));
 *
 * // In effect
 * const seat = stageManager.get(TargetIds.seat(0));
 * if (seat) {
 *   // animate seat
 * }
 * ```
 */
/**
 * Callbacks for table operations that TableScreen provides.
 * These allow sequencers to trigger table-level operations without
 * direct coupling to TableScreen.
 */
export interface TableOperations {
  /** Reset all cards (hole cards + community cards) and hide showdown buttons */
  resetTable: () => void;
  /** Start the round-robin dealing sequence */
  startDealing: (dealerSeat: number) => void;
}

class StageManagerImpl {
  /** Map of target ID to AnimatableTarget */
  private targets: Map<string, AnimatableTarget> = new Map();

  /** Debug mode - log all registrations */
  private debug = false;

  /** Table operations callback (set by TableScreen) */
  private tableOps: TableOperations | null = null;

  /**
   * Register a component as an effect target.
   * @param target - The effect target to register
   */
  register(target: AnimatableTarget): void {
    if (this.targets.has(target.targetId)) {
      log.effects.warn`StageManager: Overwriting existing target '${target.targetId}'`;
    }
    this.targets.set(target.targetId, target);
    if (this.debug) {
      log.effects.debug`StageManager: Registered '${target.targetId}'`;
    }
  }

  /**
   * Unregister a component.
   * Call this when a component is destroyed.
   * @param targetId - The target ID to unregister
   */
  unregister(targetId: string): void {
    const existed = this.targets.delete(targetId);
    if (this.debug && existed) {
      log.effects.debug`StageManager: Unregistered '${targetId}'`;
    }
  }

  /**
   * Get a target by ID.
   * @param targetId - The target ID to find
   * @returns The target or undefined if not found
   */
  get(targetId: string): AnimatableTarget | undefined {
    return this.targets.get(targetId);
  }

  /**
   * Get a seat view by seat index.
   * @param seatIndex - 0-based seat index
   * @returns The seat target or undefined
   */
  getSeat(seatIndex: number): AnimatableTarget | undefined {
    return this.get(TargetIds.seat(seatIndex));
  }

  /**
   * Get the hero's hole cards view.
   * @returns The hero cards target or undefined
   */
  getHeroCards(): AnimatableTarget | undefined {
    return this.get(TargetIds.HERO_CARDS);
  }

  /**
   * Get the community cards view.
   * @returns The community cards target or undefined
   */
  getCommunityCards(): AnimatableTarget | undefined {
    return this.get(TargetIds.COMMUNITY_CARDS);
  }

  /**
   * Get the pot view.
   * @returns The pot target or undefined
   */
  getPot(): AnimatableTarget | undefined {
    return this.get(TargetIds.POT);
  }

  /**
   * Get the dealer button view.
   * @returns The dealer button target or undefined
   */
  getDealerButton(): AnimatableTarget | undefined {
    return this.get(TargetIds.DEALER_BUTTON);
  }

  /**
   * Get the table view.
   * @returns The table target or undefined
   */
  getTable(): AnimatableTarget | undefined {
    return this.get(TargetIds.TABLE);
  }

  /**
   * Get a player's pot view by seat index.
   * @param seatIndex - 0-based seat index
   * @returns The player pot target or undefined
   */
  getPlayerPot(seatIndex: number): AnimatableTarget | undefined {
    return this.get(TargetIds.playerPot(seatIndex));
  }

  /**
   * Get all registered seats.
   * @returns Map of seat index to AnimatableTarget
   */
  getAllSeats(): Map<number, AnimatableTarget> {
    const seats = new Map<number, AnimatableTarget>();
    for (const [id, target] of this.targets) {
      if (id.startsWith('seat-')) {
        const seatIndex = parseInt(id.replace('seat-', ''), 10);
        if (!isNaN(seatIndex)) {
          seats.set(seatIndex, target);
        }
      }
    }
    return seats;
  }

  /**
   * Check if a target is registered.
   * @param targetId - The target ID to check
   */
  has(targetId: string): boolean {
    return this.targets.has(targetId);
  }

  /**
   * Get all registered target IDs.
   * Useful for debugging.
   */
  getRegisteredIds(): string[] {
    return Array.from(this.targets.keys());
  }

  /**
   * Clear all registered targets.
   * Call this when leaving a table or resetting state.
   */
  clear(): void {
    const count = this.targets.size;
    this.targets.clear();
    if (this.debug) {
      log.effects.debug`StageManager: Cleared ${count} targets`;
    }
  }

  /**
   * Enable or disable debug logging.
   */
  setDebug(enabled: boolean): void {
    this.debug = enabled;
  }

  /**
   * Get the number of registered targets.
   */
  get size(): number {
    return this.targets.size;
  }

  // ===========================================================================
  // TABLE OPERATIONS (for sequencers)
  // ===========================================================================

  /**
   * Register table operations from TableScreen.
   * Called once when TableScreen sets up its components.
   * @param ops - The table operations to register
   */
  registerTableOperations(ops: TableOperations): void {
    this.tableOps = ops;
    if (this.debug) {
      log.effects.debug`StageManager: Table operations registered`;
    }
  }

  /**
   * Unregister table operations.
   * Called when TableScreen is destroyed.
   */
  unregisterTableOperations(): void {
    this.tableOps = null;
    if (this.debug) {
      log.effects.debug`StageManager: Table operations unregistered`;
    }
  }

  /**
   * Reset the table (all cards and showdown buttons).
   * Used by GameStartSequencer at hand start.
   */
  resetTable(): void {
    if (!this.tableOps) {
      log.effects.warn`StageManager: resetTable called but no table operations registered`;
      return;
    }
    this.tableOps.resetTable();
  }

  /**
   * Start the dealing sequence.
   * Used by GameStartSequencer after reset.
   * @param dealerSeat - The dealer seat index
   */
  startDealing(dealerSeat: number): void {
    if (!this.tableOps) {
      log.effects.warn`StageManager: startDealing called but no table operations registered`;
      return;
    }
    this.tableOps.startDealing(dealerSeat);
  }

  /**
   * Check if table operations are available.
   */
  hasTableOperations(): boolean {
    return this.tableOps !== null;
  }
}

/**
 * Singleton instance of StageManager.
 * Import this to register and find effect targets.
 */
export const stageManager = new StageManagerImpl();
